// hooks/useCampaignStatusSync.ts
import { computed, ref } from 'vue';
import { useCampaignProgress } from './useCampaignProgress.js';
import {
  CAMPAIGN_STATUS,
  normalizeCampaignStatus,
  isFinalCampaignStatus,
} from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';

export function useCampaignStatusSync(
  schedulingId: number,
  initialStatus?: string
) {
  const { getProgressForScheduling } = useCampaignProgress();

  // Estado reativo local — enum CRU
  const currentStatus = ref(
    normalizeCampaignStatus(initialStatus) || CAMPAIGN_STATUS.SCHEDULED
  );

  // Dados do progresso da campanha
  const campaignData = getProgressForScheduling(schedulingId);

  // Status computado (enum CRU): prioriza o WebSocket, senão o status inicial.
  const syncedStatus = computed(() => {
    const wsStatus = campaignData.value.status;
    return normalizeCampaignStatus(wsStatus) || currentStatus.value;
  });

  // Campanha sem dados: terminal (completed/cancelled) e sem itens
  const isEmptyCampaign = computed(() => {
    const data = campaignData.value;
    const isFinished = [
      CAMPAIGN_STATUS.COMPLETED,
      CAMPAIGN_STATUS.CANCELLED,
    ].includes(syncedStatus.value);
    const hasNoData =
      data.totalItems === 0 || (data.progress === 0 && data.currentItem === 0);

    return isFinished && hasNoData;
  });

  // Confia apenas em status terminal: totalItems/progress zeram ao recarregar
  // a pagina mesmo com deliveries reais no banco. A tela de deliveries trata
  // o caso de lista vazia.
  const canAccessBroadcastList = computed(() =>
    isFinalCampaignStatus(syncedStatus.value)
  );

  // Função para atualizar status manualmente (se necessário)
  const updateStatus = (newStatus: string) => {
    currentStatus.value =
      normalizeCampaignStatus(newStatus) || CAMPAIGN_STATUS.SCHEDULED;
  };

  return {
    syncedStatus,
    canAccessBroadcastList,
    updateStatus,
    isRunning: computed(
      () => syncedStatus.value === CAMPAIGN_STATUS.RUNNING
    ),
    isCompleted: computed(() =>
      [CAMPAIGN_STATUS.COMPLETED, CAMPAIGN_STATUS.CANCELLED].includes(
        syncedStatus.value
      )
    ),
    isEmptyCampaign,
    campaignData,
  };
}
